/* eslint-disable @typescript-eslint/naming-convention */
import { createContext } from 'react';
import { I18nProvider as ReactAriaI18nProvider } from 'react-aria-components';
import { createIntl, createIntlCache } from 'react-intl';
import type { IntlShape } from 'react-intl';

import bgTranslations from '../../../intl/bg.json';
import csTranslations from '../../../intl/cs.json';
import daTranslations from '../../../intl/da.json';
import deTranslations from '../../../intl/de.json';
import elTranslations from '../../../intl/el.json';
import enINTranslations from '../../../intl/en-IN.json';
import es419Translations from '../../../intl/es-419.json';
import esTranslations from '../../../intl/es.json';
import etTranslations from '../../../intl/et.json';
import fiTranslations from '../../../intl/fi.json';
import frTranslations from '../../../intl/fr.json';
import hrTranslations from '../../../intl/hr.json';
import huTranslations from '../../../intl/hu.json';
import itTranslations from '../../../intl/it.json';
import jaTranslations from '../../../intl/ja.json';
import koTranslations from '../../../intl/ko.json';
import ltTranslations from '../../../intl/lt.json';
import lvTranslations from '../../../intl/lv.json';
import nlTranslations from '../../../intl/nl.json';
import noTranslations from '../../../intl/no.json';
import plTranslations from '../../../intl/pl.json';
import ptBRTranslations from '../../../intl/pt-BR.json';
import ptTranslations from '../../../intl/pt.json';
import roTranslations from '../../../intl/ro.json';
import ruTranslations from '../../../intl/ru.json';
import slTranslations from '../../../intl/sl.json';
import svTranslations from '../../../intl/sv.json';
import ukTranslations from '../../../intl/uk.json';
import zhCNTranslations from '../../../intl/zh-CN.json';
import zhTWTranslations from '../../../intl/zh-TW.json';

const translationsMap: Record<string, Record<string, string>> = {
  bg: bgTranslations,
  cs: csTranslations,
  da: daTranslations,
  de: deTranslations,
  el: elTranslations,
  'en-IN': enINTranslations,
  'es-419': es419Translations,
  es: esTranslations,
  et: etTranslations,
  fi: fiTranslations,
  fr: frTranslations,
  hr: hrTranslations,
  hu: huTranslations,
  it: itTranslations,
  ja: jaTranslations,
  ko: koTranslations,
  lt: ltTranslations,
  lv: lvTranslations,
  nl: nlTranslations,
  no: noTranslations,
  pl: plTranslations,
  'pt-BR': ptBRTranslations,
  pt: ptTranslations,
  ro: roTranslations,
  ru: ruTranslations,
  sl: slTranslations,
  sv: svTranslations,
  uk: ukTranslations,
  'zh-CN': zhCNTranslations,
  'zh-TW': zhTWTranslations
};

const defaultValue = createIntl({ locale: 'en', messages: {} });
export const IntlContext = createContext<IntlShape>(defaultValue);

const FusionIntlProvider = ({ intl, children }: { intl: IntlShape; children: React.ReactNode }) => (
  <IntlContext.Provider value={intl}>{children}</IntlContext.Provider>
);

const cache = createIntlCache();

export interface IntlProviderProps {
  locale: string;
}

const IntlProvider: React.FC<React.PropsWithChildren<IntlProviderProps>> = ({
  children,
  locale
}) => {
  const messages = translationsMap[locale] || {};

  const intl = createIntl(
    {
      locale,
      messages
    },
    cache
  );

  return (
    <FusionIntlProvider intl={intl}>
      <ReactAriaI18nProvider locale={locale}>{children}</ReactAriaI18nProvider>
    </FusionIntlProvider>
  );
};

export default IntlProvider;
